let initialized = false;
let initPromise = null;

const GEE_PROJECT = process.env.REACT_APP_GEE_PROJECT || '';

export const loadEEScript = () => {
  return new Promise((resolve, reject) => {
    if (window.ee) { resolve(window.ee); return; }
    const script = document.createElement('script');
    script.src = 'https://cdn.jsdelivr.net/npm/@google/earthengine@latest/build/ee_api_js.js';
    script.onload = () => resolve(window.ee);
    script.onerror = () => reject(new Error('GEE script yuklenemedi'));
    document.head.appendChild(script);
  });
};

export const initializeGEE = async (tokenOrProject) => {
  if (initialized) return true;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    try {
      const eeLib = await loadEEScript();
      await new Promise((resolve, reject) => {
        eeLib.data.authenticateViaOauth(
          tokenOrProject || GEE_PROJECT,
          () => resolve(),
          (err) => reject(new Error('GEE auth hatasi: ' + (err || 'Bilinmeyen'))),
          null,
          () => reject(new Error('GEE auth iptal edildi'))
        );
      });
      await new Promise((resolve, reject) => {
        eeLib.initialize(null, null, () => { initialized = true; resolve(); }, (err) => reject(err));
      });
      return true;
    } catch (e) {
      initPromise = null;
      throw e;
    }
  })();
  return initPromise;
};

export const initGEEWithToken = async (accessToken) => {
  if (initialized) return true;
  const eeLib = await loadEEScript();
  eeLib.data.setAuthToken('', 'Bearer', accessToken, 3600, [], () => {
    eeLib.initialize(null, null, () => { initialized = true; }, (err) => { throw err; });
  }, false);
  await new Promise(r => setTimeout(r, 1000));
  return true;
};

export const initGEEWithProject = async (projectId) => {
  if (initialized) return true;
  const eeLib = await loadEEScript();
  await new Promise((resolve, reject) => {
    eeLib.data.authenticateViaOauth(
      projectId,
      () => {
        eeLib.initialize(null, null, () => { initialized = true; resolve(); }, reject);
      },
      reject,
      ['https://www.googleapis.com/auth/earthengine.readonly'],
      () => reject(new Error('Kullanici auth iptal etti'))
    );
  });
  return true;
};

export const computeNDVI = async (coordinates, dateStart, dateEnd, cloudMax, ndviThreshold) => {
  const eeLib = window.ee;
  if (!eeLib || !initialized) throw new Error('GEE baslatilmadi');

  const park = eeLib.Geometry.Polygon([coordinates]);

  const s2 = eeLib.ImageCollection('COPERNICUS/S2_SR')
    .filterBounds(park)
    .filterDate(dateStart || '2024-04-01', dateEnd || '2024-10-31')
    .filter(eeLib.Filter.lt('CLOUDY_PIXEL_PERCENTAGE', cloudMax || 40));

  const maskS2 = (image) => {
    const scl = image.select('SCL');
    const mask = scl.neq(3).and(scl.neq(8)).and(scl.neq(9)).and(scl.neq(10)).and(scl.neq(11));
    return image.updateMask(mask).divide(10000);
  };

  const s2Masked = s2.map(maskS2);
  const image = s2Masked.median().clip(park);
  const ndvi = image.normalizedDifference(['B8', 'B4']).rename('NDVI');

  const threshold = ndviThreshold || 0.35;

  const ndviStats = ndvi.reduceRegion({
    reducer: eeLib.Reducer.mean().combine({ reducer2: eeLib.Reducer.minMax(), sharedInputs: true }),
    geometry: park,
    scale: 10,
    maxPixels: 1e9
  });

  const greenMask = ndvi.gte(threshold).updateMask(ndvi.gte(threshold));
  const pixelArea = eeLib.Image.pixelArea().updateMask(greenMask);
  const areaStats = pixelArea.reduceRegion({
    reducer: eeLib.Reducer.sum(),
    geometry: park,
    scale: 10,
    maxPixels: 1e9
  });

  const totalArea = eeLib.Image.pixelArea().reduceRegion({
    reducer: eeLib.Reducer.sum(),
    geometry: park,
    scale: 10,
    maxPixels: 1e9
  });

  const imageCount = s2.size();

  const [ndviResult, areaResult, totalResult, countResult] = await Promise.all([
    new Promise((res, rej) => ndviStats.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => areaStats.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => totalArea.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => imageCount.evaluate((r, e) => e ? rej(e) : res(r))),
  ]);

  const greenArea = areaResult.area || 0;
  const totalParkArea = totalResult.area || 0;

  return {
    ndviMean: ndviResult.NDVI_mean || 0,
    ndviMin: ndviResult.NDVI_min || 0,
    ndviMax: ndviResult.NDVI_max || 0,
    greenAreaM2: Math.round(greenArea),
    greenAreaHa: (greenArea / 10000).toFixed(4),
    totalAreaM2: Math.round(totalParkArea),
    totalAreaHa: (totalParkArea / 10000).toFixed(4),
    greenRatio: totalParkArea > 0 ? ((greenArea / totalParkArea) * 100).toFixed(1) : 0,
    imageCount: countResult || 0,
    threshold: threshold,
    dateRange: `${dateStart || '2024-04-01'} - ${dateEnd || '2024-10-31'}`,
  };
};

export const getNDVIMapTiles = async (coordinates, dateStart, dateEnd) => {
  const eeLib = window.ee;
  if (!eeLib || !initialized) throw new Error('GEE baslatilmadi');

  const park = eeLib.Geometry.Polygon([coordinates]);

  const s2 = eeLib.ImageCollection('COPERNICUS/S2_SR')
    .filterBounds(park)
    .filterDate(dateStart || '2024-04-01', dateEnd || '2024-10-31')
    .filter(eeLib.Filter.lt('CLOUDY_PIXEL_PERCENTAGE', 40));

  const maskS2 = (image) => {
    const scl = image.select('SCL');
    const mask = scl.neq(3).and(scl.neq(8)).and(scl.neq(9)).and(scl.neq(10)).and(scl.neq(11));
    return image.updateMask(mask).divide(10000);
  };

  const s2Masked = s2.map(maskS2);
  const image = s2Masked.median().clip(park);
  const ndvi = image.normalizedDifference(['B8', 'B4']).rename('NDVI');

  const ndviVis = ndvi.visualize({ min: -0.1, max: 0.8, palette: ['red', 'yellow', 'green', 'darkgreen'] });
  const rgbVis = image.visualize({ bands: ['B4', 'B3', 'B2'], min: 0.0, max: 0.3 });

  const [ndviMapId, rgbMapId] = await Promise.all([
    new Promise((res, rej) => ndviVis.getMap({}, (m, e) => e ? rej(e) : res(m))),
    new Promise((res, rej) => rgbVis.getMap({}, (m, e) => e ? rej(e) : res(m))),
  ]);

  return {
    ndviTileUrl: ndviMapId.urlFormat,
    rgbTileUrl: rgbMapId.urlFormat,
  };
};

export const isGEEInitialized = () => initialized;

export const computeGreenDetection = async (coordinates, dateStart, dateEnd, cloudMax) => {
  const eeLib = window.ee;
  if (!eeLib || !initialized) throw new Error('GEE baslatilmadi');

  const park = eeLib.Geometry.Polygon([coordinates]);

  const s2 = eeLib.ImageCollection('COPERNICUS/S2_SR')
    .filterBounds(park)
    .filterDate(dateStart || '2024-04-01', dateEnd || '2024-10-31')
    .filter(eeLib.Filter.lt('CLOUDY_PIXEL_PERCENTAGE', cloudMax || 40));

  const maskS2 = (image) => {
    const scl = image.select('SCL');
    const mask = scl.neq(3).and(scl.neq(8)).and(scl.neq(9)).and(scl.neq(10)).and(scl.neq(11));
    return image.updateMask(mask).divide(10000);
  };

  const s2Masked = s2.map(maskS2);
  const image = s2Masked.median().clip(park);

  const ndvi = image.normalizedDifference(['B8', 'B4']).rename('NDVI');
  const ndwi = image.normalizedDifference(['B3', 'B8']).rename('NDWI');
  const ndbi = image.normalizedDifference(['B11', 'B8']).rename('NDBI');
  const evi = image.expression(
    '2.5 * ((NIR - RED) / (NIR + 6 * RED - 7.5 * BLUE + 1))',
    { NIR: image.select('B8'), RED: image.select('B4'), BLUE: image.select('B2') }
  ).rename('EVI');

  const denseVeg = ndvi.gte(0.6);
  const healthyVeg = ndvi.gte(0.35).and(ndvi.lt(0.6));
  const sparseVeg = ndvi.gte(0.15).and(ndvi.lt(0.35));
  const water = ndwi.gt(0.0).and(ndvi.lt(0.1));
  const builtUp = ndbi.gt(0.0).and(ndvi.lt(0.15));
  const bareSoil = ndvi.gte(-0.1).and(ndvi.lt(0.15)).and(ndwi.lt(0.0)).and(ndbi.lte(0.0));

  const pixelAreaImg = eeLib.Image.pixelArea();

  const computeArea = (mask) => {
    const masked = pixelAreaImg.updateMask(mask);
    return masked.reduceRegion({ reducer: eeLib.Reducer.sum(), geometry: park, scale: 10, maxPixels: 1e9 });
  };

  const totalArea = pixelAreaImg.reduceRegion({ reducer: eeLib.Reducer.sum(), geometry: park, scale: 10, maxPixels: 1e9 });

  const ndviStats = ndvi.reduceRegion({
    reducer: eeLib.Reducer.mean().combine({ reducer2: eeLib.Reducer.minMax(), sharedInputs: true }).combine({ reducer2: eeLib.Reducer.stdDev(), sharedInputs: true }),
    geometry: park, scale: 10, maxPixels: 1e9
  });

  const eviStats = evi.reduceRegion({
    reducer: eeLib.Reducer.mean().combine({ reducer2: eeLib.Reducer.minMax(), sharedInputs: true }),
    geometry: park, scale: 10, maxPixels: 1e9
  });

  const [totalRes, denseRes, healthyRes, sparseRes, waterRes, builtRes, bareRes, ndviRes, eviRes, imgCount] = await Promise.all([
    new Promise((res, rej) => totalArea.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(denseVeg).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(healthyVeg).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(sparseVeg).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(water).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(builtUp).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(bareSoil).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => ndviStats.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => eviStats.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => s2.size().evaluate((r, e) => e ? rej(e) : res(r))),
  ]);

  const total = totalRes.area || 1;
  const pct = (val) => ((val / total) * 100).toFixed(1);

  const classes = [
    { name: 'Yogun Bitki Ortusu', area: denseRes.area || 0, color: '#166534', icon: 'tree', desc: 'NDVI >= 0.6' },
    { name: 'Saglikli Bitki', area: healthyRes.area || 0, color: '#22c55e', icon: 'leaf', desc: 'NDVI 0.35 - 0.6' },
    { name: 'Seyrek Bitki', area: sparseRes.area || 0, color: '#a3e635', icon: 'seedling', desc: 'NDVI 0.15 - 0.35' },
    { name: 'Su Yuzeyi', area: waterRes.area || 0, color: '#3b82f6', icon: 'water', desc: 'NDWI > 0, NDVI < 0.1' },
    { name: 'Yapili Alan', area: builtRes.area || 0, color: '#ef4444', icon: 'building', desc: 'NDBI > 0, NDVI < 0.15' },
    { name: 'Ciplak Toprak', area: bareRes.area || 0, color: '#d97706', icon: 'ground', desc: 'NDVI < 0.15, NDWI < 0' },
  ];

  const totalGreen = (denseRes.area || 0) + (healthyRes.area || 0) + (sparseRes.area || 0);

  return {
    totalAreaM2: Math.round(total),
    totalGreenM2: Math.round(totalGreen),
    greenPercentage: pct(totalGreen),
    classes: classes.map(c => ({ ...c, area: Math.round(c.area), percentage: pct(c.area) })),
    indices: {
      ndviMean: ndviRes.NDVI_mean || 0,
      ndviMin: ndviRes.NDVI_min || 0,
      ndviMax: ndviRes.NDVI_max || 0,
      ndviStdDev: ndviRes.NDVI_stdDev || 0,
      eviMean: eviRes.EVI_mean || 0,
      eviMin: eviRes.EVI_min || 0,
      eviMax: eviRes.EVI_max || 0,
    },
    imageCount: imgCount || 0,
    dateRange: `${dateStart || '2024-04-01'} - ${dateEnd || '2024-10-31'}`,
  };
};

export const computeTypology = async (coordinates, dateStart, dateEnd, cloudMax) => {
  const eeLib = window.ee;
  if (!eeLib || !initialized) throw new Error('GEE baslatilmadi');

  const park = eeLib.Geometry.Polygon([coordinates]);

  const s2 = eeLib.ImageCollection('COPERNICUS/S2_SR')
    .filterBounds(park)
    .filterDate(dateStart || '2024-04-01', dateEnd || '2024-10-31')
    .filter(eeLib.Filter.lt('CLOUDY_PIXEL_PERCENTAGE', cloudMax || 40));

  const maskS2 = (image) => {
    const scl = image.select('SCL');
    const mask = scl.neq(3).and(scl.neq(8)).and(scl.neq(9)).and(scl.neq(10)).and(scl.neq(11));
    return image.updateMask(mask).divide(10000);
  };

  const s2Masked = s2.map(maskS2);
  const image = s2Masked.median().clip(park);

  const ndvi = image.normalizedDifference(['B8', 'B4']).rename('NDVI');
  const ndwi = image.normalizedDifference(['B3', 'B8']).rename('NDWI');
  const ndbi = image.normalizedDifference(['B11', 'B8']).rename('NDBI');

  const b5 = image.select('B5');
  const b6 = image.select('B6');
  const b7 = image.select('B7');
  const b8 = image.select('B8');
  const b11 = image.select('B11');
  const b12 = image.select('B12');

  const reNdvi = image.normalizedDifference(['B8', 'B5']).rename('RE_NDVI');
  const nirSwirRatio = b8.divide(b11.add(0.001)).rename('NIR_SWIR');
  const canopyIdx = b7.subtract(b5).divide(b7.add(b5).add(0.001)).rename('CANOPY');
  const moistureIdx = image.normalizedDifference(['B8A', 'B11']).rename('MOISTURE');

  const isVeg = ndvi.gte(0.15);
  const isWater = ndwi.gt(0.0).and(ndvi.lt(0.1));
  const isBuilt = ndbi.gt(0.0).and(ndvi.lt(0.15));
  const isBare = ndvi.gte(-0.1).and(ndvi.lt(0.15)).and(ndwi.lt(0.0)).and(ndbi.lte(0.0));

  const grassMask = isVeg
    .and(ndvi.gte(0.15)).and(ndvi.lt(0.45))
    .and(canopyIdx.lt(0.15))
    .and(nirSwirRatio.lt(3.0));

  const shrubMask = isVeg
    .and(ndvi.gte(0.25)).and(ndvi.lt(0.55))
    .and(canopyIdx.gte(0.15)).and(canopyIdx.lt(0.30))
    .and(grassMask.not());

  const treeMask = isVeg
    .and(ndvi.gte(0.40))
    .and(canopyIdx.gte(0.20).or(nirSwirRatio.gte(3.0)))
    .and(grassMask.not()).and(shrubMask.not());

  const mixedVeg = isVeg.and(grassMask.not()).and(shrubMask.not()).and(treeMask.not());

  const hardSurface = isBuilt.or(isBare.and(ndbi.gt(-0.1)));

  const pixelAreaImg = eeLib.Image.pixelArea();
  const computeArea = (mask) => {
    const masked = pixelAreaImg.updateMask(mask);
    return masked.reduceRegion({ reducer: eeLib.Reducer.sum(), geometry: park, scale: 10, maxPixels: 1e9 });
  };

  const totalArea = pixelAreaImg.reduceRegion({ reducer: eeLib.Reducer.sum(), geometry: park, scale: 10, maxPixels: 1e9 });

  const grassNdviStats = ndvi.updateMask(grassMask).reduceRegion({ reducer: eeLib.Reducer.mean(), geometry: park, scale: 10, maxPixels: 1e9 });
  const shrubNdviStats = ndvi.updateMask(shrubMask).reduceRegion({ reducer: eeLib.Reducer.mean(), geometry: park, scale: 10, maxPixels: 1e9 });
  const treeNdviStats = ndvi.updateMask(treeMask).reduceRegion({ reducer: eeLib.Reducer.mean(), geometry: park, scale: 10, maxPixels: 1e9 });
  const moistureStats = moistureIdx.reduceRegion({
    reducer: eeLib.Reducer.mean(),
    geometry: park, scale: 10, maxPixels: 1e9
  });

  const [
    totalRes, grassRes, shrubRes, treeRes, mixedRes,
    waterRes, hardRes, bareRes,
    grassNdvi, shrubNdvi, treeNdvi, moistureRes, imgCount
  ] = await Promise.all([
    new Promise((res, rej) => totalArea.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(grassMask).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(shrubMask).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(treeMask).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(mixedVeg).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(isWater).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(hardSurface).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => computeArea(isBare).evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => grassNdviStats.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => shrubNdviStats.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => treeNdviStats.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => moistureStats.evaluate((r, e) => e ? rej(e) : res(r))),
    new Promise((res, rej) => s2.size().evaluate((r, e) => e ? rej(e) : res(r))),
  ]);

  const total = totalRes.area || 1;
  const pct = (val) => ((val / total) * 100).toFixed(1);

  const typologies = [
    { id: 'grass', name: 'Cim / Cimen', area: grassRes.area || 0, color: '#86efac', avgNdvi: grassNdvi.NDVI || 0, desc: 'Kisa bitki ortusu, cim alanlari', icon: '🌱' },
    { id: 'shrub', name: 'Cali / Calilk', area: shrubRes.area || 0, color: '#f59e0b', avgNdvi: shrubNdvi.NDVI || 0, desc: 'Orta boylu bitki, cali ve funda', icon: '🌿' },
    { id: 'tree', name: 'Agac / Orman', area: treeRes.area || 0, color: '#166534', avgNdvi: treeNdvi.NDVI || 0, desc: 'Yuksek kanopi, agac ve orman', icon: '🌳' },
    { id: 'mixed', name: 'Karisik Bitki', area: mixedRes.area || 0, color: '#a3e635', avgNdvi: 0, desc: 'Siniflandirilamamis bitki ortusu', icon: '🌾' },
    { id: 'water', name: 'Su Yuzeyi', area: waterRes.area || 0, color: '#3b82f6', avgNdvi: 0, desc: 'Gol, havuz, sulama kanali', icon: '💧' },
    { id: 'hard', name: 'Sert Zemin', area: hardRes.area || 0, color: '#94a3b8', avgNdvi: 0, desc: 'Yol, bina, beton, asfalt', icon: '🏗️' },
    { id: 'bare', name: 'Ciplak Toprak', area: bareRes.area || 0, color: '#d97706', avgNdvi: 0, desc: 'Bitkisiz toprak alan', icon: '🏜️' },
  ];

  const totalGreen = (grassRes.area || 0) + (shrubRes.area || 0) + (treeRes.area || 0) + (mixedRes.area || 0);

  return {
    totalAreaM2: Math.round(total),
    totalGreenM2: Math.round(totalGreen),
    greenPercentage: pct(totalGreen),
    typologies: typologies.map(t => ({ ...t, area: Math.round(t.area), percentage: pct(t.area) })),
    moisture: moistureRes.MOISTURE || 0,
    imageCount: imgCount || 0,
    dateRange: `${dateStart || '2024-04-01'} - ${dateEnd || '2024-10-31'}`,
  };
};

export const getTypologyMapTiles = async (coordinates, dateStart, dateEnd) => {
  const eeLib = window.ee;
  if (!eeLib || !initialized) throw new Error('GEE baslatilmadi');

  const park = eeLib.Geometry.Polygon([coordinates]);
  const s2 = eeLib.ImageCollection('COPERNICUS/S2_SR')
    .filterBounds(park)
    .filterDate(dateStart || '2024-04-01', dateEnd || '2024-10-31')
    .filter(eeLib.Filter.lt('CLOUDY_PIXEL_PERCENTAGE', 40));

  const maskS2 = (image) => {
    const scl = image.select('SCL');
    const mask = scl.neq(3).and(scl.neq(8)).and(scl.neq(9)).and(scl.neq(10)).and(scl.neq(11));
    return image.updateMask(mask).divide(10000);
  };

  const image = s2.map(maskS2).median().clip(park);
  const ndvi = image.normalizedDifference(['B8', 'B4']).rename('NDVI');
  const ndwi = image.normalizedDifference(['B3', 'B8']).rename('NDWI');
  const ndbi = image.normalizedDifference(['B11', 'B8']).rename('NDBI');
  const b5 = image.select('B5');
  const b7 = image.select('B7');
  const b8 = image.select('B8');
  const b11 = image.select('B11');
  const canopyIdx = b7.subtract(b5).divide(b7.add(b5).add(0.001));
  const nirSwirRatio = b8.divide(b11.add(0.001));

  const isVeg = ndvi.gte(0.15);
  const grassMask = isVeg.and(ndvi.gte(0.15)).and(ndvi.lt(0.45)).and(canopyIdx.lt(0.15)).and(nirSwirRatio.lt(3.0));
  const shrubMask = isVeg.and(ndvi.gte(0.25)).and(ndvi.lt(0.55)).and(canopyIdx.gte(0.15)).and(canopyIdx.lt(0.30)).and(grassMask.not());
  const treeMask = isVeg.and(ndvi.gte(0.40)).and(canopyIdx.gte(0.20).or(nirSwirRatio.gte(3.0))).and(grassMask.not()).and(shrubMask.not());

  // 0=unclassified, 1=grass, 2=shrub, 3=tree, 4=mixed, 5=water, 6=hard, 7=bare
  const classified = eeLib.Image(0)
    .where(ndvi.gte(-0.1).and(ndvi.lt(0.15)).and(ndwi.lt(0)).and(ndbi.lte(0)), 7)
    .where(ndbi.gt(0).and(ndvi.lt(0.15)), 6)
    .where(ndwi.gt(0).and(ndvi.lt(0.1)), 5)
    .where(isVeg, 4)
    .where(shrubMask, 2)
    .where(grassMask, 1)
    .where(treeMask, 3)
    .clip(park);

  const classVis = classified.visualize({
    min: 0, max: 7,
    palette: ['#e2e8f0', '#86efac', '#f59e0b', '#166534', '#a3e635', '#3b82f6', '#94a3b8', '#d97706']
  });

  const mapId = await new Promise((res, rej) => classVis.getMap({}, (m, e) => e ? rej(e) : res(m)));

  return { typologyTileUrl: mapId.urlFormat };
};

export const getDetectionMapTiles = async (coordinates, dateStart, dateEnd) => {
  const eeLib = window.ee;
  if (!eeLib || !initialized) throw new Error('GEE baslatilmadi');

  const park = eeLib.Geometry.Polygon([coordinates]);
  const s2 = eeLib.ImageCollection('COPERNICUS/S2_SR')
    .filterBounds(park)
    .filterDate(dateStart || '2024-04-01', dateEnd || '2024-10-31')
    .filter(eeLib.Filter.lt('CLOUDY_PIXEL_PERCENTAGE', 40));

  const maskS2 = (image) => {
    const scl = image.select('SCL');
    const mask = scl.neq(3).and(scl.neq(8)).and(scl.neq(9)).and(scl.neq(10)).and(scl.neq(11));
    return image.updateMask(mask).divide(10000);
  };

  const image = s2.map(maskS2).median().clip(park);
  const ndvi = image.normalizedDifference(['B8', 'B4']).rename('NDVI');
  const ndwi = image.normalizedDifference(['B3', 'B8']).rename('NDWI');
  const ndbi = image.normalizedDifference(['B11', 'B8']).rename('NDBI');

  const classified = eeLib.Image(0)
    .where(ndvi.gte(-0.1).and(ndvi.lt(0.15)).and(ndwi.lt(0)).and(ndbi.lte(0)), 1)  // bare soil
    .where(ndbi.gt(0).and(ndvi.lt(0.15)), 2)  // built-up
    .where(ndwi.gt(0).and(ndvi.lt(0.1)), 3)   // water
    .where(ndvi.gte(0.15).and(ndvi.lt(0.35)), 4)  // sparse veg
    .where(ndvi.gte(0.35).and(ndvi.lt(0.6)), 5)   // healthy veg
    .where(ndvi.gte(0.6), 6)  // dense veg
    .clip(park);

  const classVis = classified.visualize({
    min: 0, max: 6,
    palette: ['#94a3b8', '#d97706', '#ef4444', '#3b82f6', '#a3e635', '#22c55e', '#166534']
  });

  const ndviVis = ndvi.visualize({ min: -0.1, max: 0.8, palette: ['red', 'yellow', 'green', 'darkgreen'] });

  const [classMapId, ndviMapId] = await Promise.all([
    new Promise((res, rej) => classVis.getMap({}, (m, e) => e ? rej(e) : res(m))),
    new Promise((res, rej) => ndviVis.getMap({}, (m, e) => e ? rej(e) : res(m))),
  ]);

  return {
    classifiedTileUrl: classMapId.urlFormat,
    ndviTileUrl: ndviMapId.urlFormat,
  };
};

export const geeService = {
  loadEEScript,
  initializeGEE,
  initGEEWithToken,
  initGEEWithProject,
  computeNDVI,
  getNDVIMapTiles,
  computeGreenDetection,
  getDetectionMapTiles,
  computeTypology,
  getTypologyMapTiles,
  isGEEInitialized,
};
